import { DOCUMENT } from '@angular/common';
import { DestroyRef, effect, inject, Injectable, Injector, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { SwUpdate } from '@angular/service-worker';

const POLL_INTERVAL_MS = 5 * 60 * 1000;

@Injectable()
export class PwaUpdateService {
  private readonly swUpdate = inject(SwUpdate);
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  private noticeVisible = false;

  constructor() {
    if (!this.swUpdate.isEnabled) return;

    this.swUpdate.checkForUpdate().catch(() => undefined);
    this.startPolling();

    const versionUpdate = toSignal(this.swUpdate.versionUpdates);

    effect(() => {
      const event = versionUpdate();
      if (event?.type !== 'VERSION_READY') return;

      untracked(() => void this.showUpdateNotice());
    });
  }

  private startPolling(): void {
    const id = setInterval(() => {
      this.swUpdate.checkForUpdate().catch(() => undefined);
    }, POLL_INTERVAL_MS);

    this.destroyRef.onDestroy(() => clearInterval(id));
  }

  private async showUpdateNotice(): Promise<void> {
    if (this.noticeVisible) return;
    this.noticeVisible = true;

    try {
      const { showUpdateNotice } = await import('./show-update-notice');
      const dispose = showUpdateNotice(this.injector, () => void this.applyUpdate());
      this.destroyRef.onDestroy(dispose);
    } catch (error) {
      console.error('[pwa-update] failed to show the update notice', error);
      this.noticeVisible = false;
    }
  }

  private async applyUpdate(): Promise<void> {
    await this.swUpdate.activateUpdate().catch(() => undefined);
    this.document.defaultView?.location.reload();
  }
}
